import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PARTY_MAX, type PublicUser } from '@snakefeast/shared';
import { api } from '../lib/api';
import { lobbySocket } from '../lib/lobbySocket';
import { useLobby } from '../store/lobby';
import { useSession } from '../store/session';
import { Avatar, Modal } from './ui';
import { GameIcon, Glyph } from './GameIcon';

type Friend = PublicUser & { status: 'online' | 'offline' | 'in_game' };

/** Online friends to invite; offline ones are listed but can't be picked. */
function InviteSheet({ onClose, memberIds }: { onClose: () => void; memberIds: string[] }) {
  const { t } = useTranslation();
  const presence = useLobby((s) => s.presence);
  const [friends, setFriends] = useState<Friend[] | null>(null);
  const [sent, setSent] = useState<string[]>([]);
  useEffect(() => {
    void api<{ friends: Friend[] }>('/api/friends')
      .then((f) => setFriends(f.friends))
      .catch(() => setFriends([]));
  }, []);
  const status = (f: Friend) => presence[f.id] ?? f.status;
  const invite = (id: string) => {
    lobbySocket.send({ t: 'party.invite', toUserId: id });
    setSent((s) => [...s, id]);
  };
  return (
    <Modal title={t('party.inviteTitle')} onClose={onClose}>
      {friends === null && <div className="center"><span className="spin" /></div>}
      {friends?.length === 0 && <p className="faint">{t('party.noFriends')}</p>}
      <ul className="party-invite-list">
        {friends?.filter((f) => !memberIds.includes(f.id)).map((f) => (
          <li key={f.id} className="row">
            <Avatar avatar={f.avatar} avatarUrl={f.avatarUrl} name={f.displayName} size={36} />
            <span className="grow truncate">
              {f.displayName}
              <div className="faint">{t(`party.status.${status(f)}`)}</div>
            </span>
            <button className="btn small primary" disabled={status(f) === 'offline' || sent.includes(f.id)} onClick={() => invite(f.id)}>
              {sent.includes(f.id) ? t('party.invited') : t('party.invite')}
            </button>
          </li>
        ))}
      </ul>
    </Modal>
  );
}

/** Your party on Home: who's in it, invite friends, leave. Guests (no friends list) don't see it. */
export function PartyCard() {
  const { t } = useTranslation();
  const me = useSession((s) => s.user)!;
  const party = useLobby((s) => s.party);
  const [inviting, setInviting] = useState(false);
  if (me.isGuest) return null;

  if (!party) {
    return (
      <>
        <button className="card tight interactive row party-start" onClick={() => setInviting(true)}>
          <GameIcon name="party" size={28} />
          <span className="grow">
            <strong>{t('party.startTitle')}</strong>
            <div className="faint">{t('party.startBody')}</div>
          </span>
          <span className="btn small">{t('party.invite')}</span>
        </button>
        {inviting && <InviteSheet onClose={() => setInviting(false)} memberIds={[me.id]} />}
      </>
    );
  }

  const leader = party.leaderId === me.id;
  return (
    <section className="card tight party-card" aria-label={t('party.title')}>
      <div className="row">
        <strong className="grow"><Glyph e="🎉" /> {t('party.title')} · {party.members.length}/{PARTY_MAX}</strong>
        <button className="btn ghost small" onClick={() => lobbySocket.send({ t: 'party.leave' })}>{t('party.leave')}</button>
      </div>
      <div className="party-members">
        {party.members.map((m) => (
          <div key={m.userId} className="party-member">
            <Avatar avatar={m.avatar} avatarUrl={m.avatarUrl} name={m.displayName} size={40} premium={m.isPremium} />
            <small className="truncate">{m.userId === party.leaderId ? <><Glyph e="👑" /> </> : ''}{m.userId === me.id ? t('common.you') : m.displayName}</small>
            {leader && m.userId !== me.id && (
              <button className="party-kick" aria-label={t('party.remove', { name: m.displayName })} onClick={() => lobbySocket.send({ t: 'party.kick', userId: m.userId })}>×</button>
            )}
          </div>
        ))}
        {leader && party.members.length < PARTY_MAX && (
          <button className="party-member add" onClick={() => setInviting(true)} aria-label={t('party.invite')}>
            <span>＋</span>
            <small>{t('party.invite')}</small>
          </button>
        )}
      </div>
      <p className="faint" style={{ margin: '8px 0 0' }}>{leader ? t('party.leaderHint') : t('party.memberHint')}</p>
      {inviting && <InviteSheet onClose={() => setInviting(false)} memberIds={party.members.map((m) => m.userId)} />}
    </section>
  );
}
